import { PublicClientApplication } from '@azure/msal-browser';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IsotipoRedondo from 'assets/images/logos/IsotipoRedondo.svg';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import config from 'config';
import useQuery from 'hooks/useQuery';
import useSubdomain from 'hooks/useSubdomain';
import React, { type FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  usePasswordRecoveryMutation,
  useResendMfaCodeMutation,
  useSignInUserMutation,
  useSignInWithGoogleMutation,
  useSignInWithMicrosoftMutation,
  useVerifyMfaCodeMutation,
} from 'services/api/user.api';
import { isMfaChallenge } from 'services/types/user';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  LIGHT_GREY,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { identifyPosthogUser } from 'utils/posthogUtils';
import validateEmail from 'utils/validations/validateEmail';

import { signInUser } from '../../redux/slices/user.slice';

const FONT = 'Jost, sans-serif';

// Google Identity Services (loaded on demand from accounts.google.com/gsi/client — already
// allowed by the CSP in server.js). Minimal typing for the pieces we use.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (
            parent: HTMLElement,
            options: { theme?: string; size?: string; shape?: string; text?: string; locale?: string; width?: number }
          ) => void;
        };
      };
    };
  }
}

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

// MSAL ("Sign in with Microsoft"). Built + initialized once, lazily, only when a client id is
// configured — /common accepts personal + any work/school account. The backend re-verifies the
// ID token, so the account only logs in if its email matches an existing Relif user.
//
// Redirect flow (not popup): privacy browsers (Safari ITP, Brave) sever the opener↔popup channel
// that loginPopup polls, so the popup receives the auth code but the app can never read it and the
// login silently times out. Redeeming in the same tab sidesteps that entirely. The tab returns to
// /sign-in, where handleRedirectPromise reads the response — so `${origin}/sign-in` must be
// registered as a redirect URI under the Entra app's "Single-page application" platform.
let msalInitPromise: Promise<PublicClientApplication | null> | null = null;
const getMsalInstance = (): Promise<PublicClientApplication | null> => {
  const clientId = config.microsoftSsoClientId;
  if (!clientId) return Promise.resolve(null);
  if (!msalInitPromise) {
    const instance = new PublicClientApplication({
      auth: {
        clientId,
        authority: 'https://login.microsoftonline.com/common',
        redirectUri: `${window.location.origin}/sign-in`,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
    msalInitPromise = instance.initialize().then(() => instance);
  }
  return msalInitPromise;
};

// Official Microsoft logo (four squares) — the brand-compliant mark for "Sign in with Microsoft".
const MicrosoftIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

// Official Google "G" — brand-compliant mark, shown on our custom button while the real GIS
// button stays transparently on top to own the click + token flow (see the social block below).
const GoogleIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46z"
    />
    <path fill="#FBBC05" d="M11.69 28.18a13.2 13.2 0 0 1 0-8.36v-5.7H4.34a22 22 0 0 0 0 19.76l7.35-5.7z" />
    <path
      fill="#EA4335"
      d="M24 9.5c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 2.83 29.93.5 24 .5A21.99 21.99 0 0 0 4.34 14.12l7.35 5.7C13.42 13.37 18.27 9.5 24 9.5z"
    />
  </svg>
);

// Fixed size for both social buttons so they read as one unit; 320px stays inside the card on
// mobile, 44px matches the GIS button's own height so the transparent overlay covers it.
const SOCIAL_BTN_WIDTH = 320;
const SOCIAL_BTN_HEIGHT = 44;

// Shared tonal-filled style for the Google + Microsoft buttons (design-system No-Line Rule).
const socialButtonSx = {
  bgcolor: SURFACE_CONTAINER_LOW,
  color: NAVY,
  borderRadius: RADIUS_COIN,
  fontFamily: FONT,
  fontWeight: 600,
  fontSize: '0.95rem',
  textTransform: 'none' as const,
  boxShadow: `0px 4px 12px ${alpha(NAVY, 0.06)}`,
  '&:hover': { bgcolor: LIGHT_GREY },
  transition: 'all 0.2s ease-in-out',
};

const SEVERITY_BORDER: Record<string, string> = {
  error: 'rgba(231, 35, 59, 0.35)',
  success: 'rgba(83, 183, 33, 0.35)',
  info: 'rgba(71, 188, 216, 0.35)',
  warning: 'rgba(207, 92, 9, 0.35)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_CONTAINER_LOW,
    borderRadius: RADIUS_ITEM,
    fontFamily: FONT,
    fontSize: '0.95rem',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: alpha(NAVY, 0.15) },
    '&.Mui-focused fieldset': { borderColor: alpha(NAVY, 0.3), borderWidth: '1.5px' },
    '&.Mui-focused': { bgcolor: LIGHT_GREY },
  },
  '& .MuiInputLabel-root': {
    fontFamily: FONT,
    color: alpha(NAVY, 0.5),
  },
  '& .MuiInputLabel-root.Mui-focused': { color: NAVY },
  '& .MuiFormHelperText-root': { fontFamily: FONT },
};

const SignIn: FC = () => {
  const [signIn, { data, isLoading, isSuccess, error }] = useSignInUserMutation();
  const [verifyMfa, { data: mfaData, isLoading: isVerifyingMfa, isSuccess: isMfaSuccess, error: mfaError }] =
    useVerifyMfaCodeMutation();
  const [resendMfa, { isLoading: isResendingMfa, isSuccess: isResendSuccess, error: resendError }] =
    useResendMfaCodeMutation();
  const [
    passwordRecovery,
    { isLoading: isLoadingPasswordRecovery, data: dataPasswordRecovery, isError: isErrorPasswordRecovery },
  ] = usePasswordRecoveryMutation();
  const [
    signInWithGoogle,
    { data: googleData, isLoading: isGoogleLoading, isSuccess: isGoogleSuccess, error: googleError },
  ] = useSignInWithGoogleMutation();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const [
    signInWithMicrosoft,
    { data: microsoftData, isLoading: isMicrosoftLoading, isSuccess: isMicrosoftSuccess, error: microsoftError },
  ] = useSignInWithMicrosoftMutation();
  const [msalPending, setMsalPending] = React.useState(false);
  const msalInFlightRef = useRef(false);
  // True on load when Microsoft has just redirected back here with an auth response, so we can show
  // the loading overlay through the token exchange instead of flashing the login form.
  const [processingMsRedirect, setProcessingMsRedirect] = React.useState(() =>
    /[?#&](code|error)=/.test(window.location.hash)
  );

  const query = useQuery();
  const dispatch = useDispatch();
  const passwordReset = query.get('reset');
  const { companyInfo, isValidSubdomain } = useSubdomain();

  const [step, setStep] = React.useState<'credentials' | 'mfa'>('credentials');
  const [mfaToken, setMfaToken] = React.useState<string>('');
  const [otpDigits, setOtpDigits] = React.useState<string[]>(['', '', '', '', '', '']);
  const [otpStatus, setOtpStatus] = React.useState<'idle' | 'error' | 'success'>('idle');
  const [mfaLockedOut, setMfaLockedOut] = React.useState(false);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([null, null, null, null, null, null]);

  const [noEmailError, setNoEmailError] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [invalidEmail, setInvalidEmail] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'info';
  }>({ open: false, message: '', severity: 'error' });

  const showSnackbar = useCallback(
    (message: string, severity: 'error' | 'success' | 'info' = 'error') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      return;
    }
    const recaptchaToken = await getReCaptchaToken();
    await signIn({
      recaptchaToken,
      email: emailInput,
      password: formData.get('password')?.toString() || '',
    });
  };

  const submitMfa = useCallback(
    (code: string) => {
      if (!mfaToken || code.length !== 6) return;
      verifyMfa({ mfaToken, code }).catch(() => {});
    },
    [mfaToken, verifyMfa]
  );

  const handleOtpChange = (index: number, raw: string) => {
    if (otpStatus !== 'idle') setOtpStatus('idle');
    const sanitized = raw.replace(/\D/g, '');
    if (!sanitized) {
      setOtpDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }
    // Paste handling: full code pasted into one box
    if (sanitized.length > 1) {
      const chars = sanitized.slice(0, 6).split('');
      setOtpDigits((prev) => {
        const next = [...prev];
        chars.forEach((c, i) => {
          if (index + i < 6) next[index + i] = c;
        });
        return next;
      });
      const focusIdx = Math.min(index + chars.length, 5);
      otpRefs.current[focusIdx]?.focus();
      if (index + chars.length >= 6) {
        const completed = (otpDigits.slice(0, index).join('') + chars.join('')).slice(0, 6);
        if (completed.length === 6) submitMfa(completed);
      }
      return;
    }
    // Compute completed code outside the state updater so the updater stays pure.
    // React StrictMode double-invokes updaters; calling submitMfa inside would fire the
    // verify request twice — the second call would land after clearMfaCode and 401.
    let completedCode: string | null = null;
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = sanitized;
      const completed = next.join('');
      if (completed.length === 6 && next.every((d) => d !== '')) {
        completedCode = completed;
      }
      return next;
    });
    if (index < 5) otpRefs.current[index + 1]?.focus();
    if (completedCode) submitMfa(completedCode);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleResend = () => {
    if (!mfaToken) return;
    resendMfa({ mfaToken }).catch(() => {});
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setMfaToken('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpStatus('idle');
    setMfaLockedOut(false);
  };

  const handlePasswordRecovery = async () => {
    if (!emailInput) {
      setNoEmailError(true);
      return;
    }
    if (!validateEmail(emailInput)) {
      setInvalidEmail(true);
      return;
    }
    try {
      const recaptchaToken = await getReCaptchaToken();
      await passwordRecovery({ email: emailInput, recaptchaToken }).unwrap();
    } catch (err) {
      console.log(err);
    }
  };

  const inputHelperText = () => {
    if (noEmailError) return 'Por favor ingrese su email para recuperar su contraseña';
    if (invalidEmail) return 'Por favor ingrese un email válido';
    return '';
  };

  useEffect(() => {
    if (isSuccess && data) {
      if (isMfaChallenge(data)) {
        setMfaToken(data.mfaToken);
        setStep('mfa');
        setOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } else {
        dispatch(signInUser(data));
        identifyPosthogUser(data);
      }
    }
  }, [data, isSuccess, dispatch]);

  useEffect(() => {
    if (isMfaSuccess && mfaData) {
      setOtpStatus('success');
      dispatch(signInUser(mfaData));
      identifyPosthogUser(mfaData);
    }
  }, [mfaData, isMfaSuccess, dispatch]);

  // Load Google Identity Services and render the official button (Google branding rules
  // require their button — deliberate design-system exception). Re-runs when returning
  // from the MFA step because the container div remounts.
  useEffect(() => {
    if (!config.googleSsoClientId || step !== 'credentials') return undefined;
    const clientId = config.googleSsoClientId;
    let cancelled = false;

    const renderGoogleButton = () => {
      const gsi = window.google?.accounts?.id;
      const container = googleButtonRef.current;
      if (cancelled || !gsi || !container) return;
      gsi.initialize({
        client_id: clientId,
        callback: (response) => {
          signInWithGoogle({ credential: response.credential }).catch(() => {});
        },
      });
      // GIS appends its widget without clearing the parent; clear first so a second render
      // (StrictMode double-invoke, or returning from the MFA step) never stacks two buttons.
      container.replaceChildren();
      gsi.renderButton(container, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'signin_with',
        locale: 'es',
        width: SOCIAL_BTN_WIDTH,
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return undefined;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    // Drop the dead tag on load failure (blocked/offline) so a later render can retry instead
    // of forever waiting on a 'load' that never fires against the same orphaned <script>.
    const handleError = () => script.remove();
    script.addEventListener('load', renderGoogleButton);
    script.addEventListener('error', handleError);
    if (!existing) {
      script.src = GSI_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      script.removeEventListener('load', renderGoogleButton);
      script.removeEventListener('error', handleError);
    };
  }, [step, signInWithGoogle]);

  useEffect(() => {
    if (isGoogleSuccess && googleData) {
      dispatch(signInUser(googleData));
      identifyPosthogUser(googleData);
    }
  }, [googleData, isGoogleSuccess, dispatch]);

  useEffect(() => {
    if (googleError && 'data' in googleError) showSnackbar(String(googleError.data), 'error');
  }, [googleError, showSnackbar]);

  const handleMicrosoftLogin = useCallback(async () => {
    // Ref guard: a second click before the disabled state commits would throw interaction_in_progress.
    if (msalInFlightRef.current) return;
    msalInFlightRef.current = true;
    setMsalPending(true);
    try {
      const msal = await getMsalInstance();
      if (!msal) {
        msalInFlightRef.current = false;
        setMsalPending(false);
        return;
      }
      // Navigates the whole tab to Microsoft; the response is handled on return by the
      // handleRedirectPromise effect below, so nothing after this line runs on success.
      await msal.loginRedirect({ scopes: ['openid', 'profile', 'email'], prompt: 'select_account' });
    } catch (err) {
      // Only reached if the redirect never starts (init/config error). Concurrent-interaction and
      // user-cancel aren't real errors; the backend-rejection path surfaces via microsoftError.
      const code = (err as { errorCode?: string })?.errorCode;
      const benign = ['user_cancelled', 'interaction_in_progress'];
      if (code && !benign.includes(code)) {
        showSnackbar('No se pudo iniciar sesión con Microsoft', 'error');
      }
      msalInFlightRef.current = false;
      setMsalPending(false);
    }
  }, [showSnackbar]);

  // Complete a Microsoft redirect-flow login: on return from Microsoft the tab lands back on
  // /sign-in with the auth response in the URL; handleRedirectPromise reads it and we exchange the
  // ID token for a session (the success/error effects below finish the flow).
  useEffect(() => {
    let cancelled = false;
    getMsalInstance()
      // navigateToLoginRequestUrl:false → resolve here with the result instead of MSAL navigating
      // the tab back to the request URL; our success effect drives the post-login redirect.
      .then((msal) => msal?.handleRedirectPromise({ navigateToLoginRequestUrl: false }))
      .then((result) => {
        if (cancelled) return undefined;
        if (result?.idToken) return signInWithMicrosoft({ credential: result.idToken }).unwrap();
        setProcessingMsRedirect(false);
        return undefined;
      })
      .catch(() => {
        if (!cancelled) setProcessingMsRedirect(false);
      });
    return () => {
      cancelled = true;
    };
  }, [signInWithMicrosoft]);

  useEffect(() => {
    if (isMicrosoftSuccess && microsoftData) {
      dispatch(signInUser(microsoftData));
      identifyPosthogUser(microsoftData);
    }
  }, [microsoftData, isMicrosoftSuccess, dispatch]);

  useEffect(() => {
    if (microsoftError && 'data' in microsoftError) showSnackbar(String(microsoftError.data), 'error');
  }, [microsoftError, showSnackbar]);

  useEffect(() => {
    if (error && 'data' in error) showSnackbar(String(error.data), 'error');
  }, [error, showSnackbar]);

  useEffect(() => {
    if (mfaError && 'data' in mfaError) {
      const status = 'status' in mfaError ? mfaError.status : undefined;
      const dataStr = String(mfaError.data);
      console.debug('[MFA] verify error', { status, data: dataStr });
      const isLockout = status === 429 || /demasiados intentos/i.test(dataStr);
      if (isLockout) {
        setMfaLockedOut(true);
        setOtpStatus('error');
        return;
      }
      setOtpDigits(['', '', '', '', '', '']);
      showSnackbar(dataStr, 'error');
      setOtpStatus('error');
      otpRefs.current[0]?.focus();
    }
  }, [mfaError, showSnackbar]);

  useEffect(() => {
    if (resendError && 'data' in resendError) showSnackbar(String(resendError.data), 'error');
  }, [resendError, showSnackbar]);

  useEffect(() => {
    if (isResendSuccess) showSnackbar('Código reenviado a tu email', 'success');
  }, [isResendSuccess, showSnackbar]);

  useEffect(() => {
    if (isErrorPasswordRecovery) showSnackbar('Hubo un error al recuperar la contraseña', 'error');
  }, [isErrorPasswordRecovery, showSnackbar]);

  useEffect(() => {
    if (dataPasswordRecovery) showSnackbar(dataPasswordRecovery.message, 'success');
  }, [dataPasswordRecovery, showSnackbar]);

  useEffect(() => {
    if (passwordReset) showSnackbar('Se ha cambiado tu contraseña exitosamente', 'success');
  }, [passwordReset, showSnackbar]);

  return (
    <>
      <LoadingOverlay
        visible={
          isLoading ||
          isLoadingPasswordRecovery ||
          isVerifyingMfa ||
          isGoogleLoading ||
          isMicrosoftLoading ||
          msalPending ||
          processingMsRedirect
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            color: NAVY,
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: RADIUS_CARD,
            boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.18)',
            border: `1px solid ${SEVERITY_BORDER[snackbar.severity] ?? SEVERITY_BORDER.error}`,
            '& .MuiAlert-action .MuiIconButton-root': { color: alpha(NAVY, 0.5) },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', minHeight: '100vh', fontFamily: FONT, bgcolor: NAVY }}>
        {/* ── Left hero panel ── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: { md: '46%', lg: '50%' },
            bgcolor: '#fff',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { md: 5, lg: 7 },
          }}
        >
          {/* Headline */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              textAlign: 'center',
              px: { md: 2, lg: 6 },
            }}
          >
            {/* Logo */}
            <img
              src="/logo.webp"
              alt="Relif"
              height="80"
              style={{ objectFit: 'contain', display: 'block', marginBottom: '32px' }}
            />
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 'clamp(2rem, 2.8vw, 3.2rem)',
                lineHeight: 1.2,
                color: NAVY,
              }}
            >
              Inteligencia{' '}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    width: '100%',
                    height: '3px',
                    bgcolor: NAVY,
                  },
                }}
              >
                Artificial
              </Box>{' '}
              especialista en tu industria.
            </Typography>
          </Box>

          {/* Copyright */}
          <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: alpha(NAVY, 0.25) }}>
            © {new Date().getFullYear()} Relif · Todos los derechos reservados
          </Typography>
        </Box>

        {/* ── Right form panel ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, sm: 5 },
            flexDirection: 'column',
          }}
        >
          {/* Form card */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              p: { xs: 3.5, sm: 4.5 },
              boxShadow: '0px 24px 64px rgba(21, 24, 58, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Title with isotipo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: -2 }}>
              <img src={IsotipoRedondo} alt="Relif" height="48" style={{ objectFit: 'contain' }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.75rem', color: NAVY, lineHeight: 1.2 }}>
                Bienvenido
              </Typography>
            </Box>
            {isValidSubdomain && companyInfo.logoUrl && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <img
                  alt={companyInfo.companyName}
                  src={companyInfo.logoUrl}
                  height="40"
                  style={{ objectFit: 'contain', maxWidth: 120 }}
                />
              </Box>
            )}
            <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: alpha(NAVY, 0.45) }}>
              {step === 'credentials'
                ? 'Ingresa a tu espacio de trabajo'
                : `Enviamos un código de autenticación de 6 dígitos a tu correo ${emailInput}, escribelo a continuación`}
            </Typography>

            {step === 'credentials' && (
              <>
                <Box
                  component="form"
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    handleSubmit(e).catch((err) => {
                      console.error(err);
                    });
                  }}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <TextField
                    id="email"
                    variant="outlined"
                    fullWidth
                    label="Email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    error={noEmailError || invalidEmail}
                    helperText={inputHelperText()}
                    sx={fieldSx}
                  />
                  <TextField
                    id="password"
                    variant="outlined"
                    fullWidth
                    label="Contraseña"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    sx={fieldSx}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    sx={{
                      bgcolor: YELLOW,
                      color: NAVY,
                      borderRadius: RADIUS_COIN,
                      py: 1.5,
                      fontFamily: FONT,
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: `0 4px 8px ${alpha(YELLOW, 0.25)}`,
                      mt: 0.5,
                      '&:hover': {
                        bgcolor: alpha(YELLOW, 0.85),
                      },
                      transition: 'all 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    }}
                  >
                    Iniciar sesión
                  </Button>
                </Box>

                {(config.googleSsoClientId || config.microsoftSsoClientId) && (
                  <>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        color: alpha(NAVY, 0.4),
                        fontSize: '0.8rem',
                        textAlign: 'center',
                      }}
                    >
                      o
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
                      {config.googleSsoClientId && (
                        // This Box IS the visible button; the real GIS button sits transparently on
                        // top (opacity 0) so it still owns the click + token flow (GIS branding rules
                        // forbid a fully custom Google button). Hovering the overlay bubbles :hover up
                        // to this Box, so it highlights exactly like the Microsoft button.
                        <Box
                          sx={{
                            ...socialButtonSx,
                            position: 'relative',
                            width: SOCIAL_BTN_WIDTH,
                            height: SOCIAL_BTN_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            cursor: 'pointer',
                          }}
                        >
                          <GoogleIcon />
                          Inicia sesión con Google
                          <Box ref={googleButtonRef} sx={{ position: 'absolute', inset: 0, opacity: 0 }} />
                        </Box>
                      )}
                      {config.microsoftSsoClientId && (
                        <Button
                          onClick={handleMicrosoftLogin}
                          disabled={msalPending || isMicrosoftLoading}
                          startIcon={<MicrosoftIcon />}
                          sx={{ ...socialButtonSx, width: SOCIAL_BTN_WIDTH, height: SOCIAL_BTN_HEIGHT }}
                        >
                          Inicia sesión con Microsoft
                        </Button>
                      )}
                    </Box>
                  </>
                )}

                <Button
                  onClick={handlePasswordRecovery}
                  sx={{
                    fontFamily: FONT,
                    color: alpha(NAVY, 0.4),
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    borderRadius: RADIUS_ITEM,
                    '&:hover': { bgcolor: alpha(NAVY, 0.05), color: alpha(NAVY, 0.7) },
                    transition: 'all 0.15s ease-in-out',
                  }}
                >
                  Olvidé mi contraseña
                </Button>
              </>
            )}

            {step === 'mfa' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  {otpDigits.map((digit, idx) => (
                    <TextField
                      key={`otp-${idx}`}
                      inputRef={(el: HTMLInputElement | null) => {
                        otpRefs.current[idx] = el;
                      }}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e as React.KeyboardEvent<HTMLInputElement>)}
                      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                      inputMode="numeric"
                      disabled={mfaLockedOut}
                      slotProps={{
                        htmlInput: {
                          maxLength: idx === 0 ? 6 : 1,
                          readOnly: mfaLockedOut,
                          style: {
                            textAlign: 'center',
                            fontFamily: FONT,
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: NAVY,
                            padding: '14px 0',
                          },
                        },
                      }}
                      sx={{
                        ...fieldSx,
                        width: 48,
                        '& .MuiOutlinedInput-root': {
                          ...fieldSx['& .MuiOutlinedInput-root'],
                          height: 56,
                          ...(otpStatus === 'error' && {
                            bgcolor: 'rgba(231, 35, 59, 0.06)',
                            '& fieldset': { borderColor: 'rgba(231, 35, 59, 0.50)', borderWidth: '1.5px' },
                            '&:hover fieldset': { borderColor: 'rgba(231, 35, 59, 0.7)' },
                            '&.Mui-focused fieldset': { borderColor: 'rgba(231, 35, 59, 0.85)', borderWidth: '1.5px' },
                            '&.Mui-focused': { bgcolor: 'rgba(231, 35, 59, 0.04)' },
                          }),
                          ...(otpStatus === 'success' && {
                            bgcolor: 'rgba(83, 183, 33, 0.10)',
                            '& fieldset': { borderColor: 'rgba(83, 183, 33, 0.55)', borderWidth: '1.5px' },
                            '&.Mui-focused fieldset': { borderColor: 'rgba(83, 183, 33, 0.85)', borderWidth: '1.5px' },
                            '&.Mui-focused': { bgcolor: 'rgba(83, 183, 33, 0.10)' },
                          }),
                        },
                      }}
                    />
                  ))}
                </Box>

                {mfaLockedOut && (
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.85rem',
                      fontWeight: 550,
                      color: 'rgb(231, 35, 59)',
                      textAlign: 'center',
                      mt: -0.5,
                    }}
                  >
                    Demasiados intentos incorrectos. Vuelve a iniciar sesión.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    onClick={handleBackToCredentials}
                    sx={{
                      fontFamily: FONT,
                      color: mfaLockedOut ? NAVY : alpha(NAVY, 0.4),
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      borderRadius: RADIUS_ITEM,
                      '&:hover': {
                        color: mfaLockedOut ? NAVY : alpha(NAVY, 0.7),
                      },
                    }}
                  >
                    Volver
                  </Button>
                  <Button
                    onClick={handleResend}
                    disabled={isResendingMfa || mfaLockedOut}
                    sx={{
                      fontFamily: FONT,
                      color: alpha(NAVY, 0.4),
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      borderRadius: RADIUS_ITEM,
                      '&:hover': { bgcolor: alpha(NAVY, 0.05), color: alpha(NAVY, 0.7) },
                    }}
                  >
                    Reenviar código
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SignIn;
